import { generateComponentId } from '../utils/IdGenerator';
import { mockApiService } from './MockApiService';

export type NavigationOperation = 'create' | 'update' | 'delete';
export type NavigationTarget = 'group' | 'page';

export interface NavigationOperationPayload {
  operation: NavigationOperation;
  target: NavigationTarget;
  id?: string;       
  parentId?: string; 
  data?: any;        
}

/**
 * Performs navigation operations (create, update, delete) for groups and pages
 * @param modelId The UI model ID
 * @param payload The operation details
 * @returns Promise with the result
 */
export const performNavigationOperation = async (
  modelId: string,
  payload: NavigationOperationPayload
): Promise<{success: boolean; data?: any; error?: string}> => {
  try {
    const { operation, target, id, parentId, data } = payload;

    if (!operation || !target) {
      return { success: false, error: 'Missing required fields: operation and target' };
    }

    if ((operation === 'update' || operation === 'delete') && !id) {
      return { success: false, error: 'ID is required for update and delete operations' };
    }

    if (target === 'page' && !parentId && operation !== 'delete') {
      return { success: false, error: 'Parent group ID is required for page operations' };
    }

    if (!modelId) {
      modelId = mockApiService.getCurrentModelId() || '';
      if (!modelId) {
        return { success: false, error: 'No model ID available' };
      }
    }

    const model = await mockApiService.getUIModel(modelId);
    if (!model) {
      console.error(`Model not found with ID: ${modelId}`);
      
      const models = await mockApiService.getUIModels();
      if (models && models.length > 0) {
        const fallbackModel = models[0];
        console.log(`Using fallback model: ${fallbackModel._id}`);
        modelId = fallbackModel._id;
        
        if (operation === 'update') {
          await mockApiService.updateUIModel(fallbackModel._id, {
            updatedAt: new Date().toISOString()
          });
          
          return { 
            success: true, 
            data: { id: id || '', fallbackUsed: true },
            error: 'Used fallback model, original model not found'
          };
        }
      } else {
        return { success: false, error: 'No models available' };
      }
    }

    // Perform the appropriate operation
    switch (operation) {
      case 'create':
        if (target === 'group') {
          // Create a new group
          const generatedId = id || generateComponentId('group');
          const result = await mockApiService.addGroup(modelId, {
            id: generatedId,
            name: data?.name || 'New Group',
            order: model?.layout?.groups?.length || 0,
            orientation: data?.orientation || 'horizontal',
            configuration: {
                showTitle: data?.showTitle !== undefined ? data.showTitle : true,
            },
            pages: []
          });
          return { success: result, data: { id: generatedId } };
        } else {
          if (!parentId) {
            return { success: false, error: 'Parent group ID is required for page creation' };
          }
          
          const group = model?.layout?.groups?.find(g => g.id === parentId);
          if (!group) {
            return { success: false, error: 'Parent group not found' };
          }

          const pageId = id || generateComponentId('page');
          const result = await mockApiService.addTab(modelId, parentId, {
            id: pageId,
            name: data?.title || 'New Page',
            order: group.pages?.length || 0,
            configuration: {
              autoAdvance: data?.autoAdvance || false,
            },
            canvas: { containers: [] }
          });
          return { success: result, data: { id: pageId, parentId } };
        }

      case 'update':
        if (target === 'group') {
          if (!id) {
            return { success: false, error: 'Group ID is required for update' };
          }
          
          const group = model?.layout?.groups?.find(g => g.id === id);
          if (!group) {
            return { success: false, error: 'Group not found' };
          }

          const updatedGroup = {
            ...group,
            name: data?.name !== undefined ? data.name : group.name,
            orientation: data?.orientation !== undefined ? data.orientation : group.orientation,
            configuration: {
              ...group.configuration,
              showTitle: data?.showTitle !== undefined ? data.showTitle : group.configuration?.showTitle,
            }
          };

          try {
            const result = await mockApiService.updateGroup(modelId, id, updatedGroup);
            return { success: result, data: { id } };
          } catch (err) {
            console.error("Error updating group:", err);
            return { success: false, error: 'Failed to update group' };
          }
        } else {
          if (!id || !parentId) {
            return { success: false, error: 'Page ID and parent group ID are required for update' };
          }
          
          const group = model?.layout?.groups?.find(g => g.id === parentId);
          if (!group) {
            return { success: false, error: 'Parent group not found' };
          }

          const page = group.pages?.find(t => t.id === id);
          if (!page) {
            return { success: false, error: 'Page not found' };
          }

          const updatedPage = {
            ...page,
            name: data?.title !== undefined ? data.title : page.name,
            configuration: {
                ...page.configuration,
                autoAdvance: data?.autoAdvance !== undefined ? data.autoAdvance : page.configuration?.autoAdvance
            }
          };

          try {
            const result = await mockApiService.updateTab(modelId, parentId, id, updatedPage);
            return { success: result, data: { id, parentId } };
          } catch (err) {
            console.error("Error updating page:", err);
            return { success: false, error: 'Failed to update page' };
          }
        }

      case 'delete':
        if (target === 'group') {
          if (!id) {
            return { success: false, error: 'Group ID is required for deletion' };
          }
          
          try {
            const result = await mockApiService.deleteGroup(modelId, id);
            return { success: result, data: { id } };
          } catch (err) {
            console.error("Error deleting group:", err);
            return { success: false, error: 'Failed to delete group' };
          }
        } else {
          if (!id || !parentId) {
            return { success: false, error: 'Page ID and parent group ID are required for deletion' };
          }
          
          try {
            const result = await mockApiService.deleteTab(modelId, parentId, id);
            return { success: result, data: { id, parentId } };
          } catch (err) {
            console.error("Error deleting page:", err);
            return { success: false, error: 'Failed to delete page' };
          }
        }

      default:
        return { success: false, error: 'Invalid operation' };
    }
  } catch (error) {
    console.error('Navigation operation failed:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error during navigation operation' 
    };
  }
};

/**
 * Gets the canvas data for a specific page
 * @param modelId The UI model ID
 * @param groupId The group ID
 * @param pageId The page ID
 * @returns Promise with the canvas data
 */
export const getPageCanvas = async (
  modelId: string,
  groupId: string,
  pageId: string
): Promise<{success: boolean; data?: any; error?: string}> => {
  try {
    if (!modelId) {
      modelId = mockApiService.getCurrentModelId() || '';
      if (!modelId) {
        return { success: false, error: 'No model ID available' };
      }
    }

    try {
      const containers = await mockApiService.getContainers(modelId, groupId, pageId);
      return { success: true, data: { containers } };
    } catch (err) {
      console.error("Error fetching containers:", err);
      return { success: false, error: 'Failed to fetch containers' };
    }
  } catch (error) {
    console.error('Failed to fetch page canvas:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error fetching page canvas' 
    };
  }
};

const NavigationApiService = {
  performNavigationOperation,
  getPageCanvas
};

export default NavigationApiService;